import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getPlayers, getPostes, addPlayer, updatePlayer, deletePlayer } from '../api';

// Configuration de l'API
const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3001/api'
  : `${window.location.protocol}//${window.location.hostname}/api`;

export default function GestionJoueurs() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [postes, setPostes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // État pour le formulaire d'ajout/modification
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    id_poste: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Charger les données au montage
  useEffect(() => {
    // Changer le titre de la page
    document.title = 'ESMANAGER - GESTION JOUEURS';
    
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Charger les joueurs et postes en parallèle
      const [playersData, postesData] = await Promise.all([
        getPlayers(),
        getPostes()
      ]);

      setPlayers(playersData);
      setPostes(postesData);

    } catch (error) {
      console.error('Erreur chargement données:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Ouvrir le formulaire d'ajout
  const handleAddPlayer = () => {
    setEditingPlayer(null);
    setFormData({ firstname: '', lastname: '', id_poste: '' });
    setFormErrors({});
    setDialogOpen(true);
  };

  // Ouvrir le formulaire de modification
  const handleEditPlayer = (player) => {
    setEditingPlayer(player);
    setFormData({
      firstname: player.firstname || '',
      lastname: (player.lastname || '').toUpperCase(),
      id_poste: player.id_poste || ''
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  // Fermer le formulaire
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPlayer(null);
    setFormData({ firstname: '', lastname: '', id_poste: '' });
    setFormErrors({});
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};

    if (!formData.firstname.trim()) {
      errors.firstname = 'Le prénom est obligatoire';
    }

    if (!formData.lastname.trim()) {
      errors.lastname = 'Le nom est obligatoire';
    }

    if (!formData.id_poste) {
      errors.id_poste = 'Le poste est obligatoire';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const playerData = {
        firstname: formData.firstname.trim(),
        lastname: formData.lastname.trim().toUpperCase(),
        id_poste: parseInt(formData.id_poste)
      };

      if (editingPlayer) {
        // Modification
        await updatePlayer(editingPlayer.rowid, playerData);
      } else {
        // Ajout
        await addPlayer(playerData);
      }

      // Recharger les données
      await loadData();
      handleCloseDialog();

    } catch (error) {
      console.error('Erreur sauvegarde joueur:', error);
      setError('Erreur lors de la sauvegarde du joueur');
    }
  };

  // Supprimer un joueur
  const handleDeletePlayer = async (playerId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce joueur ?')) return;

    try {
      await deletePlayer(playerId);

      // Recharger les données
      await loadData();

    } catch (error) {
      console.error('Erreur suppression joueur:', error);
      setError('Erreur lors de la suppression du joueur');
    }
  };

  // Gestion des changements du formulaire
  const handleInputChange = (field, value) => {
    // Transformer automatiquement le nom de famille en majuscules
    const processedValue = field === 'lastname' ? value.toUpperCase() : value;

    setFormData(prev => ({ ...prev, [field]: processedValue }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Récupérer le nom du poste
  const getPosteName = (idPoste) => {
    const poste = postes.find(p => p.rowid === idPoste);
    return poste ? poste.label : 'Non défini';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          bgcolor: '#fff',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <IconButton
            onClick={() => navigate('/')}
            sx={{
              p: 1,
              minWidth: 'auto',
              width: 'auto'
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <img src="/assets/logo_esma.png" alt="logo" style={{ height: '40px', marginLeft: 'auto', marginRight: 'auto' }} />
          <Box sx={{ width: '48px' }} />
        </Box>

      {/* Erreur */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Bouton ajouter */}
      <Box sx={{ mb: 3, px: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPlayer}
          sx={{
            bgcolor: '#211b3f',
            '&:hover': { bgcolor: '#f57c00' },
            fontWeight: 'bold'
          }}
        >
          Ajouter un joueur
        </Button>
      </Box>

      {/* Liste des joueurs */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', px: 2 }}>
        Liste des joueurs ({players.length})
      </Typography>

      <TableContainer component={Paper} elevation={0} sx={{ width: '100%', overflowX: 'auto', borderRadius: 0 }}>
            <Table sx={{ width: '100%', tableLayout: 'auto' }}>
              <TableHead sx={{ bgcolor: '#211b3f' }}>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      color: 'white', 
                      fontWeight: 'bold',
                      position: 'sticky',
                      left: 0,
                      bgcolor: '#211b3f',
                      zIndex: 2,
                      width: '25%',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Nom
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '25%' }}>Prénom</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '25%' }}>Poste</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '25%', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {players.map((player) => (
                  <TableRow key={player.rowid} hover>
                    <TableCell 
                      sx={{ 
                        fontWeight: 'bold', 
                        width: '25%',
                        position: 'sticky',
                        left: 0,
                        bgcolor: 'white',
                        zIndex: 1,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {player.lastname}
                    </TableCell>
                    <TableCell sx={{ width: '25%' }}>{player.firstname}</TableCell>
                    <TableCell sx={{ width: '25%' }}>
                      <Chip
                        label={getPosteName(player.id_poste)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', width: '25%' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditPlayer(player)}
                          sx={{ color: '#2196f3', padding: '4px' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeletePlayer(player.rowid)}
                          sx={{ color: '#f44336', padding: '4px' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {players.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Aucun joueur trouvé
            </Typography>
          )}

      {/* Dialog d'ajout/modification */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPlayer ? 'Modifier le joueur' : 'Ajouter un joueur'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Prénom"
              value={formData.firstname}
              onChange={(e) => handleInputChange('firstname', e.target.value)}
              error={!!formErrors.firstname}
              helperText={formErrors.firstname}
              fullWidth
              required
            />

            <TextField
              label="Nom"
              value={formData.lastname}
              onChange={(e) => handleInputChange('lastname', e.target.value)}
              error={!!formErrors.lastname}
              helperText={formErrors.lastname}
              fullWidth
              required
            />

            <FormControl fullWidth required error={!!formErrors.id_poste}>
              <InputLabel>Poste</InputLabel>
              <Select
                value={formData.id_poste}
                onChange={(e) => handleInputChange('id_poste', e.target.value)}
                label="Poste"
              >
                {postes.map((poste) => (
                  <MenuItem key={poste.rowid} value={poste.rowid}>
                    {poste.label}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.id_poste && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {formErrors.id_poste}
                </Typography>
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
          >
            {editingPlayer ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
